import { useState, useEffect } from "react";
import { contractsAPI, vehiclesAPI, driversAPI, availabilityAPI, operationalLinesAPI } from "../lib/api";
import { formatVehicleLabel, isPostServiceByDate } from "../lib/formatters";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Truck,
  User,
  Plus,
  Edit,
  Trash2,
  Check,
  AlertCircle,
  Loader2,
  Clock,
  History
} from "lucide-react";
import { cn } from "../lib/utils";

export function MultipleAssignments({ contractId, contract, canManage, onUpdate }) {
  const [assignments, setAssignments] = useState([]);
  const [requiredUnits, setRequiredUnits] = useState(1);
  const [assignedCount, setAssignedCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  // v4.59.2: operational_lines del contrato (capa operativa normalizada)
  const [operationalLines, setOperationalLines] = useState([]);
  // v4.59.2 (ajuste): línea operativa activa en el modal (para banner "Asignando línea operativa")
  const [activeLine, setActiveLine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  // v4.56.0: listas completas (incluye inactivos) para resolver/conservar históricos en edición
  const [vehiclesAll, setVehiclesAll] = useState([]);
  const [driversAll, setDriversAll] = useState([]);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [vehicleAvailable, setVehicleAvailable] = useState(null);
  const [driverAvailable, setDriverAvailable] = useState(null);
  
  const [formData, setFormData] = useState({
    vehicle_id: "",
    driver_id: "",
    start_datetime: contract?.pickup_datetime || "",
    end_datetime: contract?.end_datetime || "",
    notes: "",
    // v4.57.0: ventanas por línea
    origin: "",
    destination: "",
    vehicle_line_id: null
  });
  const [showAdvanced, setShowAdvanced] = useState(false);  // v4.57.0
  const [postServiceReason, setPostServiceReason] = useState("");

  // Post-service detection: contract.end_datetime (or pickup if missing) < now
  // v4.59.5: usa isPostServiceByDate (naive => UTC) para alinear con el backend.
  const isPostService = (() => {
    if (!contract) return false;
    const ref = contract.end_datetime || contract.pickup_datetime;
    return isPostServiceByDate(ref);
  })();

  useEffect(() => {
    if (contractId) {
      fetchData();
    }
  }, [contractId]);

  useEffect(() => {
    // Set default dates from contract
    if (contract) {
      setFormData(prev => ({
        ...prev,
        start_datetime: contract.pickup_datetime || "",
        end_datetime: contract.end_datetime || ""
      }));
    }
  }, [contract]);

  const fetchData = async () => {
    if (!contractId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setLoadError(null);
    
    // Create a timeout promise (10 seconds)
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Timeout: La carga de asignaciones tardó demasiado')), 10000);
    });
    
    try {
      // Race between actual fetch and timeout
      const result = await Promise.race([
        Promise.all([
          contractsAPI.getAssignments(contractId).catch(err => {
            console.error("Error loading assignments:", err);
            return { data: { assignments: [], required_units: 1, assigned_count: 0, pending_assignments: 0 } };
          }),
          vehiclesAPI.getAll().catch(err => {
            console.error("Error loading vehicles:", err);
            return { data: [] };
          }),
          driversAPI.getAll().catch(err => {
            console.error("Error loading drivers:", err);
            return { data: [] };
          })
        ]),
        timeoutPromise
      ]);
      
      const [assignmentsRes, vehiclesRes, driversRes] = result;
      
      setAssignments(assignmentsRes.data?.assignments || []);
      setRequiredUnits(assignmentsRes.data?.required_units || 1);
      setAssignedCount(assignmentsRes.data?.assigned_count || 0);
      setPendingCount(assignmentsRes.data?.pending_assignments || 0);
      setVehiclesAll(vehiclesRes.data || []);
      setDriversAll(driversRes.data || []);
      setVehicles((vehiclesRes.data || []).filter(v => v.status === "activo"));
      setDrivers((driversRes.data || []).filter(d => d.status === "activo"));
      
      // v4.59.2: cargar capa operativa derivada (no bloquea la carga)
      operationalLinesAPI.getByContract(contractId, { expand_quantity: false })
        .then(res => setOperationalLines(res.data?.operational_lines || []))
        .catch(() => setOperationalLines([]));
    } catch (error) {
      console.error("Error loading assignments data:", error);
      setLoadError(error.message || "No se pudieron cargar las asignaciones. Intenta refrescar la página.");
    } finally {
      setLoading(false);
    }
  };

  const checkAvailability = async (resourceType, resourceId) => {
    if (!resourceId || !formData.start_datetime || !formData.end_datetime) return;
    
    setCheckingAvailability(true);
    try {
      const response = await availabilityAPI.check({
        resource_type: resourceType,
        resource_id: resourceId,
        start_datetime: formData.start_datetime,
        end_datetime: formData.end_datetime,
        exclude_contract_id: contractId
      });
      
      if (resourceType === "vehicle") {
        setVehicleAvailable(response.data.available);
        if (!response.data.available && response.data.conflicts?.length > 0) {
          const conflict = response.data.conflicts[0];
          toast.warning(`Unidad ocupada: ${conflict.contract_folio || "Otro contrato"}`);
        }
      } else {
        setDriverAvailable(response.data.available);
        if (!response.data.available && response.data.conflicts?.length > 0) {
          const conflict = response.data.conflicts[0];
          toast.warning(`Chofer ocupado: ${conflict.contract_folio || "Otro contrato"}`);
        }
      }
    } catch (error) {
      console.error("Error checking availability:", error);
    } finally {
      setCheckingAvailability(false);
    }
  };

  const handleVehicleChange = (vehicleId) => {
    setFormData(prev => ({ ...prev, vehicle_id: vehicleId }));
    setVehicleAvailable(null);
    if (vehicleId) {
      checkAvailability("vehicle", vehicleId);
    }
  };

  const handleDriverChange = (driverId) => {
    setFormData(prev => ({ ...prev, driver_id: driverId }));
    setDriverAvailable(null);
    if (driverId) {
      checkAvailability("driver", driverId);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.vehicle_id || !formData.driver_id) {
      toast.error("Selecciona unidad y chofer");
      return;
    }
    
    if (!formData.start_datetime || !formData.end_datetime) {
      toast.error("Especifica fecha/hora de inicio y fin");
      return;
    }
    
    if (isPostService && (!postServiceReason || postServiceReason.trim().length < 10)) {
      toast.error("Modificación post-servicio requiere motivo (mínimo 10 caracteres)");
      return;
    }

    try {
      // v4.57.0: normalize origin/destination (empty string → null = inherit)
      const cleanedOrigin = (formData.origin || "").trim() || null;
      const cleanedDestination = (formData.destination || "").trim() || null;
      const basePayload = {
        vehicle_id: formData.vehicle_id,
        driver_id: formData.driver_id,
        start_datetime: formData.start_datetime,
        end_datetime: formData.end_datetime,
        notes: formData.notes,
        origin: cleanedOrigin,
        destination: cleanedDestination,
        vehicle_line_id: formData.vehicle_line_id || null
      };
      const payload = isPostService
        ? (editingAssignment && editingAssignment.source === "v2"
            ? { vehicle_id: formData.vehicle_id, driver_id: formData.driver_id, notes: formData.notes, post_service_change_reason: postServiceReason.trim() }
            : { ...basePayload, post_service_change_reason: postServiceReason.trim() })
        : basePayload;

      // v4.57.0: promotion on-edit — if editing a derived (non-v2) assignment, POST instead of PUT
      const isDerived = editingAssignment && editingAssignment.source && editingAssignment.source !== "v2";
      if (editingAssignment && !isDerived) {
        await contractsAPI.updateAssignment2(contractId, editingAssignment.id, payload);
        toast.success(isPostService ? "Asignación post-servicio actualizada (OS regenerada)" : "Asignación actualizada");
      } else {
        await contractsAPI.createAssignment(contractId, payload);
        toast.success(isPostService ? "Asignación post-servicio creada (OS regenerada)" : (isDerived ? "Asignación personalizada creada" : "Asignación creada"));
      }
      setDialogOpen(false);
      resetForm();
      setActiveLine(null);
      fetchData();
      // v4.59.2: refresh operational_lines tras asignar
      operationalLinesAPI.getByContract(contractId, { expand_quantity: false })
        .then(res => setOperationalLines(res.data?.operational_lines || []))
        .catch(() => {});
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("[MultipleAssignments] submit error:", error.response?.status, error.response?.data, "payload:", formData);
      const msg = error.response?.data?.detail || "Error al guardar asignación";
      toast.error(msg);
    }
  };

  const handleEdit = (assignment) => {
    setEditingAssignment(assignment);
    setFormData({
      vehicle_id: assignment.vehicle_id || "",
      driver_id: assignment.driver_id || "",
      start_datetime: assignment.start_datetime || contract?.pickup_datetime || "",
      end_datetime: assignment.end_datetime || contract?.end_datetime || "",
      notes: assignment.notes || "",
      // v4.57.0: cargar valores propios o vacíos (vacío = hereda)
      origin: assignment.origin || "",
      destination: assignment.destination || "",
      vehicle_line_id: assignment.vehicle_line_id || null
    });
    // Abrir sección avanzada si la asignación tiene personalización
    const hasCustomWindow = assignment.window_inherited === false;
    const hasCustomOrigin = assignment.origin_inherited === false || assignment.destination_inherited === false;
    setShowAdvanced(hasCustomWindow || hasCustomOrigin);
    setVehicleAvailable(null);
    setDriverAvailable(null);
    setDialogOpen(true);
  };

  // v4.57.0: restaurar herencia (origin/destination null, start/end = contract)
  const restoreInheritance = () => {
    setFormData(prev => ({
      ...prev,
      origin: "",
      destination: "",
      start_datetime: contract?.pickup_datetime || prev.start_datetime,
      end_datetime: contract?.end_datetime || prev.end_datetime
    }));
    toast.info("Valores restaurados: hereda del contrato");
  };

  const handleDelete = async (assignmentId) => {
    let reason = null;
    if (isPostService) {
      reason = window.prompt("Motivo del cambio post-servicio (mín. 10 caracteres):") || "";
      if (reason.trim().length < 10) {
        toast.error("Se requiere motivo de mínimo 10 caracteres");
        return;
      }
    } else if (!window.confirm("¿Eliminar esta asignación?")) {
      return;
    }
    
    try {
      await contractsAPI.deleteAssignment(contractId, assignmentId, reason);
      toast.success(isPostService ? "Asignación eliminada (post-servicio, OS regenerada)" : "Asignación eliminada");
      fetchData();
      if (onUpdate) onUpdate();
    } catch (error) {
      const msg = error.response?.data?.detail || "Error al eliminar asignación";
      toast.error(msg);
    }
  };

  const resetForm = () => {
    setEditingAssignment(null);
    setFormData({
      vehicle_id: "",
      driver_id: "",
      start_datetime: contract?.pickup_datetime || "",
      end_datetime: contract?.end_datetime || "",
      notes: "",
      origin: "",
      destination: "",
      vehicle_line_id: null
    });
    setShowAdvanced(false);
    setVehicleAvailable(null);
    setDriverAvailable(null);
    setPostServiceReason("");
  };

  const openNewDialog = () => {
    resetForm();
    setActiveLine(null);
    setDialogOpen(true);
  };

  // v4.59.2 (ajuste final): abrir el modal CONTEXTUALIZADO a una operational_line específica
  const openLineAssignDialog = (ol) => {
    resetForm();
    setActiveLine(ol);
    setFormData({
      vehicle_id: "",
      driver_id: "",
      start_datetime: ol.effective_start || "",
      end_datetime: ol.effective_end || "",
      notes: "",
      // Solo prellenamos origin/destination si fueron explícitamente personalizadas
      // (line_origin/destination) — si heredan globales, dejamos vacío para que el backend herede
      origin: ol.origin_source === "line" ? (ol.effective_origin || "") : "",
      destination: ol.destination_source === "line" ? (ol.effective_destination || "") : "",
      vehicle_line_id: ol.vehicle_line_id || null,
    });
    setDialogOpen(true);
  };

  const hasVehicleLines = operationalLines.length > 0 && operationalLines.some(o => o.vehicle_line_id);

  const formatDateTime = (dt) => {
    if (!dt) return "-";
    try {
      return new Date(dt).toLocaleString("es-MX", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return dt;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
          <p className="text-sm text-slate-500 mt-2">Cargando asignaciones...</p>
        </CardContent>
      </Card>
    );
  }

  if (loadError) {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-6 text-center">
          <AlertCircle className="w-8 h-8 mx-auto mb-2 text-amber-500" />
          <p className="text-amber-700 mb-3">{loadError}</p>
          <Button variant="outline" size="sm" onClick={fetchData}>
            Reintentar
          </Button>
        </CardContent>
      </Card>
    );
  }

  // v4.56.0: helpers para selectores (nueva selección solo activos; edición conserva inactivo histórico)
  const getVehiclesForForm = (currentId = null) => {
    if (currentId && !vehicles.find(v => v.id === currentId)) {
      const historical = vehiclesAll.find(v => v.id === currentId);
      if (historical) return [...vehicles, historical];
    }
    return vehicles;
  };
  const getDriversForForm = (currentId = null) => {
    if (currentId && !drivers.find(d => d.id === currentId)) {
      const historical = driversAll.find(d => d.id === currentId);
      if (historical) return [...drivers, historical];
    }
    return drivers;
  };
  const vehicleSelectLabel = (v) => {
    const base = formatVehicleLabel(v);
    return v?.status === "inactivo" ? `${base} (inactiva)` : base;
  };
  const driverSelectLabel = (d) => {
    const base = `${d?.name || ""} - ${d?.phone || ""}`;
    return d?.status === "inactivo" ? `${base} (inactivo)` : base;
  };

  return (
    <Card data-testid="multiple-assignments-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Truck className="w-5 h-5" />
            Asignaciones de Unidades y Choferes
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            {assignedCount} de {requiredUnits} unidad(es) asignada(s)
            {pendingCount > 0 && (
              <Badge className="ml-2 bg-amber-100 text-amber-800">
                {pendingCount} pendiente(s)
              </Badge>
            )}
            {pendingCount === 0 && assignedCount >= requiredUnits && (
              <Badge className="ml-2 bg-green-100 text-green-800">
                <Check className="w-3 h-3 mr-1" />
                Completo
              </Badge>
            )}
          </p>
        </div>
        {/* v4.59.2 (ajuste final): botón global solo visible para contratos legacy/sin operational_lines */}
        {canManage && pendingCount > 0 && !hasVehicleLines && (
          <Button onClick={openNewDialog} className="gap-2" data-testid="add-assignment-btn">
            <Plus className="w-4 h-4" />
            Nueva Asignación
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {/* v4.59.2: Capa operativa normalizada (read-only summary) */}
        {operationalLines.length > 0 && (
          <div className="mb-3 border border-slate-200 rounded-lg bg-slate-50/50 overflow-hidden" data-testid="operational-lines-summary">
            <div className="px-3 py-2 bg-slate-100 border-b border-slate-200 flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Líneas operativas (ventanas efectivas)</span>
              <span className="text-[10px] text-slate-500">v2 &gt; line_* &gt; global</span>
            </div>
            <div className="divide-y divide-slate-100">
              {operationalLines.map((ol, idx) => {
                const fmt = (d) => d ? new Date(d).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" }) : "—";
                const srcBadge = ol.window_source === "v2"
                  ? { cls: "bg-emerald-100 text-emerald-800 border-emerald-300", label: "v2" }
                  : ol.window_source === "line"
                    ? { cls: "bg-amber-100 text-amber-800 border-amber-300", label: "Personalizada" }
                    : { cls: "bg-slate-100 text-slate-600 border-slate-300", label: "Hereda" };
                return (
                  <div key={ol.vehicle_line_id || idx} className="px-3 py-2 flex items-center gap-2 text-xs" data-testid={`op-line-${idx}`}>
                    <span className="font-medium text-slate-800 min-w-[110px]">{ol.unit_label}</span>
                    <Badge variant="outline" className={cn("text-[9px] h-4 px-1.5", srcBadge.cls)}>{srcBadge.label}</Badge>
                    <span className="text-slate-600 flex-1">
                      {fmt(ol.effective_start)} → {fmt(ol.effective_end)}
                    </span>
                    {ol.is_assigned && (
                      <Badge className="bg-green-100 text-green-800 text-[9px] h-4 px-1.5">Asignada</Badge>
                    )}
                    {ol.is_pending && (
                      <Badge className="bg-amber-100 text-amber-800 text-[9px] h-4 px-1.5">Pendiente</Badge>
                    )}
                    {ol.partial_assignment && (
                      <Badge variant="outline" className="text-[9px] h-4 px-1.5 border-amber-400 text-amber-700">
                        {ol.partial_assignment.assigned}/{ol.partial_assignment.required}
                      </Badge>
                    )}
                    {/* v4.59.2 (ajuste final): botón Asignar por línea pendiente */}
                    {ol.is_pending && ol.vehicle_line_id && (
                      <Button
                        type="button"
                        size="sm"
                        variant="default"
                        className="h-6 px-2 text-[10px]"
                        onClick={() => openLineAssignDialog(ol)}
                        data-testid={`assign-line-btn-${idx}`}
                        title={`Asignar ${ol.unit_label}`}
                      >
                        <Plus className="w-3 h-3 mr-1" /> Asignar
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {isPostService && (
          <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 flex items-center gap-2" data-testid="post-service-tab-banner">
            <AlertCircle className="w-3.5 h-3.5" />
            Servicio finalizado. Las modificaciones de asignación requerirán motivo y se registran en auditoría.
          </div>
        )}
        {assignments.length === 0 ? (
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 mx-auto mb-3 text-amber-400" />
            <p className="text-slate-500">No hay asignaciones aún</p>
            {canManage && (
              <Button variant="link" onClick={openNewDialog} className="mt-2">
                Agregar primera asignación
              </Button>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">#</TableHead>
                <TableHead>Unidad</TableHead>
                <TableHead>Chofer</TableHead>
                <TableHead>Horario</TableHead>
                <TableHead>Estado</TableHead>
                {canManage && <TableHead className="text-right">Acciones</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {assignments.map((assignment) => {
                const isDerived = assignment.source && assignment.source !== "v2";
                return (
                <TableRow key={assignment.id} data-testid={`assignment-row-${assignment.id}`}>
                  <TableCell className="font-medium">{assignment.assignment_index}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Truck className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium">{assignment.vehicle?.name || "Sin asignar"}</p>
                        <p className="text-xs text-slate-500">{assignment.vehicle?.plate}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <User className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium">{assignment.driver?.name || "Sin asignar"}</p>
                        <p className="text-xs text-slate-500">{assignment.driver?.phone}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm">
                      <Clock className="w-4 h-4 text-slate-400" />
                      <div>
                        <p>{formatDateTime(assignment.start_datetime)}</p>
                        <p className="text-slate-500">→ {formatDateTime(assignment.end_datetime)}</p>
                        {/* v4.57.0: badges Hereda/Personalizada */}
                        {assignment.window_inherited === false && (
                          <Badge variant="outline" className="mt-1 text-[10px] border-amber-300 text-amber-700 bg-amber-50" data-testid="badge-custom-window">
                            Ventana personalizada
                          </Badge>
                        )}
                        {assignment.window_inherited === true && (
                          <Badge variant="outline" className="mt-1 text-[10px] border-slate-300 text-slate-500" data-testid="badge-inherited-window">
                            Hereda del contrato
                          </Badge>
                        )}
                        {(assignment.origin_inherited === false || assignment.destination_inherited === false) && (
                          <Badge variant="outline" className="mt-1 ml-1 text-[10px] border-amber-300 text-amber-700 bg-amber-50" data-testid="badge-custom-route">
                            Ruta personalizada
                          </Badge>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <Badge className={cn(
                        assignment.status === "activa" ? "bg-green-100 text-green-800" :
                        assignment.status === "completada" ? "bg-blue-100 text-blue-800" :
                        "bg-red-100 text-red-800"
                      )}>
                        {assignment.status}
                      </Badge>
                      {isDerived && (
                        <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-500" title="Asignación gestionada desde el panel de Asignación">
                          {assignment.source === "legacy_v1" ? "Legacy" : "Panel Asignación"}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  {canManage && (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {isDerived ? (
                          <span className="text-xs text-slate-400" data-testid={`assignment-readonly-${assignment.id}`}>
                            Editar en Panel de Asignación
                          </span>
                        ) : (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(assignment)}
                              data-testid={`edit-assignment-${assignment.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDelete(assignment.id)}
                              data-testid={`delete-assignment-${assignment.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Dialog for Add/Edit Assignment */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingAssignment ? "Editar Asignación" : "Nueva Asignación"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* v4.59.2 (ajuste final): banner "Asignando línea operativa" cuando se asigna una línea específica */}
            {activeLine && !editingAssignment && (
              <div
                className={cn(
                  "p-3 rounded-lg border",
                  activeLine.window_source === "line"
                    ? "bg-amber-50 border-amber-300"
                    : activeLine.window_source === "v2"
                      ? "bg-emerald-50 border-emerald-300"
                      : "bg-blue-50 border-blue-200"
                )}
                data-testid="line-context-banner"
              >
                <p className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-2">
                  <Truck className="w-4 h-4" />
                  Asignando línea operativa
                </p>
                <div className="grid grid-cols-1 gap-1 text-xs text-slate-700">
                  <div><strong>Tipo:</strong> {activeLine.unit_label || activeLine.vehicle_type_label}</div>
                  <div>
                    <strong>Ventana:</strong>{" "}
                    {(activeLine.effective_start ? new Date(activeLine.effective_start).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" }) : "—")}{" "}
                    →{" "}
                    {(activeLine.effective_end ? new Date(activeLine.effective_end).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" }) : "—")}
                  </div>
                  <div><strong>Origen:</strong> {activeLine.effective_origin || "—"}</div>
                  <div><strong>Destino:</strong> {activeLine.effective_destination || "—"}</div>
                  <div>
                    <strong>Fuente:</strong>{" "}
                    <Badge variant="outline" className={cn(
                      "text-[10px] h-4 px-1.5 ml-1",
                      activeLine.window_source === "v2" && "border-emerald-400 text-emerald-700",
                      activeLine.window_source === "line" && "border-amber-400 text-amber-700",
                      activeLine.window_source === "global" && "border-slate-300 text-slate-600",
                    )}>
                      {activeLine.window_source === "v2" ? "v2" : activeLine.window_source === "line" ? "Personalizada" : "Hereda"}
                    </Badge>
                  </div>
                </div>
              </div>
            )}
            {isPostService && (
              <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg" data-testid="post-service-banner">
                <p className="text-sm font-medium text-amber-900 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Modificación post-servicio
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  Este servicio ya finalizó. Solo se permite cambiar unidad, chofer y notas. La OS se regenerará automáticamente.
                </p>
                <div className="mt-2 space-y-1">
                  <Label className="text-xs text-amber-900">Motivo del cambio (mín. 10 caracteres) *</Label>
                  <textarea
                    value={postServiceReason}
                    onChange={(e) => setPostServiceReason(e.target.value)}
                    rows={2}
                    className="w-full text-sm rounded-md border border-amber-300 bg-white px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-500"
                    placeholder="Ej: Corrección operativa solicitada por logística..."
                    data-testid="post-service-reason-input"
                    required
                  />
                  <p className="text-[11px] text-amber-700">{postServiceReason.trim().length} / 10 caracteres mínimos</p>
                </div>
              </div>
            )}

            {/* Vehicle Selection */}
            <div className="space-y-2">
              <Label htmlFor="vehicle">Unidad *</Label>
              <div className="flex gap-2 items-center">
                <Select
                  value={formData.vehicle_id}
                  onValueChange={handleVehicleChange}
                >
                  <SelectTrigger data-testid="select-vehicle" className="flex-1">
                    <SelectValue placeholder="Seleccionar unidad" />
                  </SelectTrigger>
                  <SelectContent>
                    {getVehiclesForForm(formData.vehicle_id).map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {vehicleSelectLabel(v)} ({v.capacity} pax)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {vehicleAvailable !== null && (
                  <Badge className={cn(
                    vehicleAvailable ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                  )}>
                    {vehicleAvailable ? <Check className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                  </Badge>
                )}
              </div>
            </div>

            {/* Driver Selection */}
            <div className="space-y-2">
              <Label htmlFor="driver">Chofer *</Label>
              <div className="flex gap-2 items-center">
                <Select
                  value={formData.driver_id}
                  onValueChange={handleDriverChange}
                >
                  <SelectTrigger data-testid="select-driver" className="flex-1">
                    <SelectValue placeholder="Seleccionar chofer" />
                  </SelectTrigger>
                  <SelectContent>
                    {getDriversForForm(formData.driver_id).map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {driverSelectLabel(d)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {driverAvailable !== null && (
                  <Badge className={cn(
                    driverAvailable ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                  )}>
                    {driverAvailable ? <Check className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                  </Badge>
                )}
              </div>
            </div>

            {/* Date/Time Range — disabled when post-service (only vehicle/driver/notes allowed) */}
            {!isPostService && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fecha/Hora Inicio *</Label>
                <Input
                  type="datetime-local"
                  value={formData.start_datetime}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_datetime: e.target.value }))}
                  required
                  data-testid="assignment-start-datetime"
                />
              </div>
              <div className="space-y-2">
                <Label>Fecha/Hora Fin *</Label>
                <Input
                  type="datetime-local"
                  value={formData.end_datetime}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_datetime: e.target.value }))}
                  min={formData.start_datetime}
                  required
                  data-testid="assignment-end-datetime"
                />
              </div>
            </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notas (opcional)</Label>
              <Input
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Instrucciones especiales..."
                data-testid="assignment-notes"
              />
            </div>

            {/* v4.57.0: Origen/Destino opcional (hereda contrato si vacío) */}
            {!isPostService && (
              <div className="border rounded-lg p-3 bg-slate-50">
                <button
                  type="button"
                  onClick={() => setShowAdvanced(s => !s)}
                  className="text-sm font-medium text-slate-700 flex items-center gap-1"
                  data-testid="toggle-advanced-window"
                >
                  {showAdvanced ? "▼" : "▶"} Personalizar origen / destino de esta línea (opcional)
                </button>
                {showAdvanced && (
                  <div className="mt-3 space-y-3">
                    <div className="text-xs text-slate-500">
                      Si dejas estos campos vacíos, esta asignación heredará del contrato.<br/>
                      Rango permitido: <strong>{contract?.pickup_datetime || "—"} → {contract?.end_datetime || "—"}</strong>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Origen específico</Label>
                        <Input
                          value={formData.origin}
                          onChange={(e) => setFormData(prev => ({ ...prev, origin: e.target.value }))}
                          placeholder={contract?.origin ? `Hereda: ${contract.origin}` : "Heredar del contrato"}
                          data-testid="assignment-origin"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Destino específico</Label>
                        <Input
                          value={formData.destination}
                          onChange={(e) => setFormData(prev => ({ ...prev, destination: e.target.value }))}
                          placeholder={contract?.destination ? `Hereda: ${contract.destination}` : "Heredar del contrato"}
                          data-testid="assignment-destination"
                        />
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={restoreInheritance}
                      data-testid="restore-inheritance-btn"
                    >
                      Restaurar herencia
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Availability Warning */}
            {(vehicleAvailable === false || driverAvailable === false) && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <AlertCircle className="w-4 h-4 inline mr-2" />
                Uno o más recursos no están disponibles en el horario seleccionado.
                Se recomienda ajustar las fechas o seleccionar otro recurso.
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                data-testid="save-assignment-btn"
                disabled={checkingAvailability}
              >
                {checkingAvailability && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingAssignment ? "Actualizar" : "Crear Asignación"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default MultipleAssignments;
